import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FacilityAccountDetailsSection, FACILITY_ACCOUNTS_PERMISSION } from './FacilityAccountDetails';
import {
  Button,
  Tabs,
  Spin,
  Alert,
  Empty,
  Tag,
  Space,
  notification,
  Modal,
  Drawer,
  Popconfirm,
  Segmented,
  Form,
  Select,
  Input,
  List,
  Typography,
  Switch,
  Tooltip,
  Pagination,
} from 'antd';
import {
  ArrowLeftOutlined,
  FileTextOutlined,
  UserOutlined,
  DollarOutlined,
  LockOutlined,
  CheckCircleOutlined,
  EditOutlined,
  EnvironmentOutlined,
  MailOutlined,
  TeamOutlined,
  ApartmentOutlined,
  BankOutlined,
  PlusOutlined,
  KeyOutlined,
} from '@ant-design/icons';
import lib from '../../assets/utils/lib';
import { useAuth } from '../../assets/utils/useAuth';
import { usePermissions } from '../../assets/utils/usePermissions';
import { useNavigationTree } from '../navigation/useNavigationTree';
import { FacilityData } from '../compoinents/FacilityData';
import DashboardSection from '../dashboard/components/DashboardSection';
import DashboardHero from '../dashboard/components/DashboardHero';
import { ADMIN_REVIEW_MODAL_CLASS, hasMetricValue } from '../dashboard/utils';
import StaffProfileEditor from '../dash/StaffProfileEditor';
import {
  listRoles,
  assignRoleToFacilityUser,
  removeRoleFromFacilityUser,
} from '../dash/rbacApi';
import {
  toFacilityProviderRecord,
  isFacilityActive,
  isFacilityListed,
  getFacilityOnboardingStatusMeta,
  mergeFacilityCounts,
} from './facilityUtils';
import FacilityBranchesTab from './FacilityBranchesTab';
import { roleOptionsFor, branchNoticeMessage } from './branchUsers';
import './facilities-dashboard.css';

const getFacilityRecordId = (f) => f?.id ?? f?.facility_id;

const getFacilityName = (f) => f?.name ?? f?.facility_name ?? 'Facility';

// Monogram identity: two initials over a colour that is stable per facility, so
// the same place is recognisable at a glance when paging through the directory.
const MONOGRAM_SKIP = new Set(['the', 'of', 'and', 'at', 'a', 'an']);
const MONOGRAM_COLORS = [
  '#3497D3', '#722ed1', '#49B870', '#0ea5e9',
  '#f59e0b', '#e11d48', '#14b8a6', '#8b5cf6',
];

const getFacilityInitials = (f) => {
  const words = String(f?.name ?? f?.facility_name ?? '')
    .trim()
    .split(/\s+/)
    .filter((w) => w && !MONOGRAM_SKIP.has(w.toLowerCase()));
  if (words.length === 0) return '?';
  return words.slice(0, 2).map((w) => w[0]).join('').toUpperCase();
};

const getMonogramColor = (f) => {
  const seed = String(getFacilityRecordId(f) ?? getFacilityName(f));
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) % 9973;
  return MONOGRAM_COLORS[hash % MONOGRAM_COLORS.length];
};
const getFacilityCode = (f) => f?.facility_code ?? f?.code ?? '—';

const formatDate = (value, fallback = 'Not set') => {
  if (!value) return fallback;
  if (typeof value === 'string' && value.startsWith('0')) return fallback;
  const d = new Date(typeof value === 'string' ? value.replace(' ', 'T') : value);
  if (Number.isNaN(d.getTime())) return fallback;
  return d.toLocaleDateString(undefined, { month: 'short', day: '2-digit', year: 'numeric' });
};

const formatDateTime = (value, fallback = '—') => {
  if (!value) return fallback;
  const d = new Date(typeof value === 'string' ? value.replace(' ', 'T') : value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString(undefined, { month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatCount = (value) => {
  if (value == null || value === '') return '—';
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString() : String(value);
};

const fieldOrDash = (...values) => {
  for (const v of values) {
    if (v != null && v !== '') return v;
  }
  return '—';
};

function FacilityDetail() {
  const { facilityId } = useParams();
  const { user } = useAuth();
  const { hasAnyPermission, hasAnyPermissionWithLegacy, canStayOnModule } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state;
  // Two shapes arrive here: the directory sends { facility, listContext }, while
  // other callers (global search) still send the bare record. A record never has
  // a listContext key, so its presence is what tells the two apart.
  const navFacility = locationState?.listContext ? locationState.facility : locationState;
  const navListContext = locationState?.listContext ?? null;

  const [facility, setFacility] = useState(navFacility || null);
  // Stepping to a sibling keeps this component mounted, so the directory slice
  // has to live in state and be refreshed from each new navigation.
  const [listContext, setListContext] = useState(navListContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('info');
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [onboardingLoading, setOnboardingLoading] = useState(false);

  const [users, setUsers] = useState([]);
  const [usersPage, setUsersPage] = useState(1);
  const [usersPageSize, setUsersPageSize] = useState(10);
  const [usersLoading, setUsersLoading] = useState(false);
  // Staff archive (spec §6): the Users tab can list archived accounts.
  const [showArchivedUsers, setShowArchivedUsers] = useState(false);
  const [userArchiveSavingId, setUserArchiveSavingId] = useState(null);
  const [usersError, setUsersError] = useState(null);
  // The Users tab covers the facility's group, like the Facility Portal's
  // "Managing staff for": the main and each branch, listed and created
  // independently. null = this facility.
  const [usersGroup, setUsersGroup] = useState(null);
  const [usersScopeId, setUsersScopeId] = useState(null);
  const usersFacilityId = usersScopeId || facilityId;

  const [tariffs, setTariffs] = useState([]);
  const [tariffsLoading, setTariffsLoading] = useState(false);
  const [tariffsError, setTariffsError] = useState(null);
  const [activeTariff, setActiveTariff] = useState(null);
  const [, setActiveTariffLoading] = useState(false);
  const [setActiveLoadingId, setSetActiveLoadingId] = useState(null);

  const [accesses, setAccesses] = useState([]);
  const [accessesLoading, setAccessesLoading] = useState(false);
  const [accessesError, setAccessesError] = useState(null);

  const [userDrawerOpen, setUserDrawerOpen] = useState(false);
  const [activeFacilityUser, setActiveFacilityUser] = useState(null);
  const [userProfileSaving, setUserProfileSaving] = useState(false);
  const [userStatusSavingId, setUserStatusSavingId] = useState(null);

  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [createUserSaving, setCreateUserSaving] = useState(false);
  const [createUserForm] = Form.useForm();
  const createTargetId = Form.useWatch('target_facility_id', createUserForm);
  const [passwordTarget, setPasswordTarget] = useState(null);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordForm] = Form.useForm();

  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [roleModalUser, setRoleModalUser] = useState(null);
  const [roleModalLoading, setRoleModalLoading] = useState(false);
  const [facilityRoles, setFacilityRoles] = useState([]);
  const [facilityRolesLoading, setFacilityRolesLoading] = useState(false);
  const [roleForm] = Form.useForm();

  // Permission-gated rather than role-id gated. These were hardcoded staff role
  // id lists, which meant the capability could not be granted or revoked without
  // a code change — and deactivating a facility could not be separated from
  // editing one. Both now come from RBAC; see
  // sqls/migrations/20260901_facility_status_permissions.sql.
  // Facility editors see account details read-only; finance edits them.
  const canViewAccountDetails = hasAnyPermission([
    FACILITY_ACCOUNTS_PERMISSION, 'facilities.manage', 'facilities.update', 'facilities.read_all', 'claims.payment', 'admin.*',
  ]);
  const canManageProfile = useMemo(
    () => hasAnyPermission(['facilities.manage', 'facilities.update', 'admin.*']),
    [hasAnyPermission],
  );
  const canActivateDisable = useMemo(
    () => hasAnyPermission(['facilities.activate', 'facilities.deactivate', 'admin.*']),
    [hasAnyPermission],
  );
  const [listedSaving, setListedSaving] = useState(false);

  const handleListedToggle = useCallback(async (nextChecked) => {
    if (!facilityId) return;
    setListedSaving(true);
    const previous = facility;
    // Optimistic: the toggle should feel immediate, not wait on a round trip.
    setFacility((f) => (f ? { ...f, listed: nextChecked ? 1 : 0, facility_listed: nextChecked ? 1 : 0 } : f));
    try {
      const resp = await lib.updateFacilityV2(facilityId, { listed: nextChecked });
      const body = resp?.data || resp;
      if (body?.error) {
        throw new Error(body.message || 'Unable to update directory visibility');
      }
      notification.success({
        message: nextChecked
          ? 'Facility will show on the website, client portal, and mobile app'
          : 'Facility hidden from the website, client portal, and mobile app',
        duration: 2.5,
      });
    } catch (e) {
      setFacility(previous);
      notification.error({
        message: e?.response?.data?.message || e?.message || 'Unable to update directory visibility',
      });
    } finally {
      setListedSaving(false);
    }
  }, [facilityId, facility]);
  // Handing out a facility credential is its own capability, not a side effect
  // of being able to edit a facility — and certainly not of being able to see
  // the facility list. facilities.manage stays accepted so nobody who could do
  // this yesterday loses it, but the new permission is what makes it grantable
  // (and revocable) on its own. Mirrors the gate in public/v2.php.
  const canSendLoginDetails = useMemo(
    () => hasAnyPermissionWithLegacy(
      ['facilities.send_login_details', 'facilities.manage', 'admin.*'], [1, 2],
    ),
    [hasAnyPermissionWithLegacy],
  );
  const canManageFacilityUsers = useMemo(
    () => hasAnyPermissionWithLegacy(['facilities.manage_staff', 'admin.*'], [1, 2]),
    [hasAnyPermissionWithLegacy],
  );
  // Setting someone's password hands over account access, so it is separable
  // from editing that user's record.
  const canSetFacilityUserPassword = useMemo(
    () => hasAnyPermissionWithLegacy(
      ['facilities.set_user_password', 'facilities.manage_staff', 'admin.*'], [1, 2],
    ),
    [hasAnyPermissionWithLegacy],
  );
  // Mirrors the backend gate on the branch write endpoints in public/v2.php.
  const canManageBranches = useMemo(
    () =>
      hasAnyPermissionWithLegacy(
        ['facilities.branches.manage', 'facilities.manage', 'admin.*'],
        [1, 2],
      ),
    [hasAnyPermissionWithLegacy],
  );
  // Matches the backend's actual gate on POST/DELETE /v2/rbac/users/{userType}/{userId}/roles
  // (requireManage in v2.php) — broader than facilities.manage_staff, so gated separately.
  const canManageFacilityRoles = useMemo(
    () => hasAnyPermissionWithLegacy(['admin.role_management', 'admin.*'], [1]),
    [hasAnyPermissionWithLegacy],
  );

  const providerRecord = useMemo(
    () => toFacilityProviderRecord(facility, facilityId),
    [facility, facilityId]
  );

  const facilityEmail = useMemo(
    () =>
      String(
        facility?.contact_email
        || facility?.facility_email
        || facility?.email
        || providerRecord.contact_email
        || providerRecord.facility_email
        || '',
      ).trim(),
    [facility, providerRecord],
  );

  const sendLoginDetails = useCallback(async ({ force = true } = {}) => {
    if (!facilityId) return;
    if (!facilityEmail) {
      notification.warning({
        message: 'Add an email on the facility profile before sending login details',
        placement: 'topRight',
      });
      setShowProfileModal(true);
      return;
    }

    setOnboardingLoading(true);
    try {
      const parsed = lib.parseApiEnvelope(
        await lib.onboardFacilityV2(facilityId, { force }),
      );
      const status =
        parsed.data?.onboarding_status
        || parsed.data?.onboarding?.onboarding_status
        || null;
      const statusMeta = getFacilityOnboardingStatusMeta(status);

      if (!parsed.ok && status !== 'already_has_password') {
        notification.error({
          message: parsed.message || 'Failed to send login details',
          description: status ? statusMeta.label : undefined,
          placement: 'topRight',
        });
        return;
      }

      if (status === 'already_has_password' && !force) {
        Modal.confirm({
          title: 'Facility already has a password',
          content: 'Regenerate a new password and resend the welcome email?',
          okText: 'Regenerate & send',
          cancelText: 'Cancel',
          onOk: () => sendLoginDetails({ force: true }),
        });
        return;
      }

      notification.success({
        message: parsed.message || 'Login details sent',
        description: status ? statusMeta.label : undefined,
        placement: 'topRight',
      });
    } catch (e) {
      notification.error({
        message: e?.response?.data?.message || e?.message || 'Failed to send login details',
        placement: 'topRight',
      });
    } finally {
      setOnboardingLoading(false);
    }
  }, [facilityEmail, facilityId]);

  const confirmSendLoginDetails = useCallback(() => {
    if (!facilityEmail) {
      sendLoginDetails({ force: true });
      return;
    }
    Modal.confirm({
      title: 'Send login details?',
      content: (
        <span>
          This regenerates the portal password for <strong>{facilityEmail}</strong> and emails
          the new credentials. The password is never shown in Care Admin.
        </span>
      ),
      okText: 'Send login details',
      cancelText: 'Cancel',
      onOk: () => sendLoginDetails({ force: true }),
    });
  }, [facilityEmail, sendLoginDetails]);

  useEffect(() => {
    if (navListContext) setListContext(navListContext);
  }, [navListContext]);

  // Same reason: without this the previous facility's details stay on screen
  // until the fetch for the new one lands.
  useEffect(() => {
    if (navFacility && String(getFacilityRecordId(navFacility)) === String(facilityId)) {
      setFacility(navFacility);
    }
  }, [facilityId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Directory pager -----------------------------------------------------
  // Position within the directory listing this page was opened from. The rows
  // carried over cover one page; stepping past either end refetches the
  // adjacent page with the same query, so the walk keeps the directory's order.
  const listRows = useMemo(
    () => (Array.isArray(listContext?.rows) ? listContext.rows : []),
    [listContext],
  );
  const listPage = Number(listContext?.page) || 1;
  const listPageSize = Number(listContext?.pageSize) || 20;
  const listTotal = Number(listContext?.total) || 0;
  const listIndex = useMemo(
    () => listRows.findIndex((r) => String(getFacilityRecordId(r)) === String(facilityId)),
    [listRows, facilityId],
  );
  // 1-based position across the whole filtered result set, not just this page.
  const listPosition = listIndex >= 0 ? (listPage - 1) * listPageSize + listIndex + 1 : 0;
  const hasPrevFacility = listIndex > 0 || (listIndex === 0 && listPage > 1);
  const hasNextFacility =
    listIndex >= 0
    && (listIndex + 1 < listRows.length || (listTotal > 0 && listPosition < listTotal));

  const goToFacility = useCallback((record, context) => {
    const id = getFacilityRecordId(record);
    if (id == null || id === '') return;
    // replace, not push: Back must still return to the directory rather than
    // walking back through every facility stepped through along the way.
    navigate(`/facilities/${id}`, {
      state: { facility: record, listContext: context },
      replace: true,
    });
  }, [navigate]);

  // -1 / 1 while that direction is fetching an adjacent page, 0 when idle — so
  // only the button actually being used shows a spinner.
  const [stepping, setStepping] = useState(0);

  const stepFacility = useCallback(async (delta) => {
    if (listIndex < 0 || stepping !== 0) return;
    const sibling = listRows[listIndex + delta];
    if (sibling) {
      goToFacility(sibling, listContext);
      return;
    }
    // Off the end of the carried page: pull the adjacent one in the same order.
    const targetPage = listPage + delta;
    if (targetPage < 1) return;
    setStepping(delta);
    try {
      const resp = await lib.getFacilitiesV2({
        ...(listContext?.query || {}),
        page: targetPage,
        limit: listPageSize,
      });
      const body = resp?.data;
      const rows = Array.isArray(body?.data) ? body.data : [];
      if (rows.length === 0) {
        notification.info({
          message: 'No more facilities in this list',
          placement: 'topRight',
        });
        return;
      }
      // Forward continues at the top of the next page, backward at the bottom
      // of the previous one — either way the order reads unbroken.
      goToFacility(delta > 0 ? rows[0] : rows[rows.length - 1], {
        ...listContext,
        page: body?.pagination?.page ?? targetPage,
        pageSize: body?.pagination?.limit ?? listPageSize,
        total: body?.pagination?.total ?? listTotal,
        rows,
      });
    } catch (e) {
      notification.error({
        message: e?.response?.data?.message || e?.message || 'Failed to load the adjacent facility',
        placement: 'topRight',
      });
    } finally {
      setStepping(0);
    }
  }, [listIndex, stepping, listRows, listContext, listPage, listPageSize, listTotal, goToFacility]);

  // Going back must return to the list AS THE USER LEFT IT — filters, sort,
  // and page live in the list's query string. History back restores them, but
  // only to the page the directory was left on; once the pager has walked onto
  // a later page, that entry is stale and the list URL has to be rebuilt.
  const listUrl = listContext?.listUrl;
  const listUrlPage = useMemo(
    () => Number(new URLSearchParams(listUrl || '').get('page')) || 1,
    [listUrl],
  );
  const backToListUrl = useMemo(() => {
    if (listUrl == null) return null;
    const params = new URLSearchParams(listUrl);
    if (listPage > 1) params.set('page', String(listPage));
    else params.delete('page');
    const qs = params.toString();
    return qs ? `/facilities?${qs}` : '/facilities';
  }, [listUrl, listPage]);

  const goBackToList = useCallback(() => {
    // Popping history is the best restore when it lands on the right slice —
    // it brings back scroll position too. It only goes stale once the pager
    // has crossed a page boundary, and then the rebuilt URL is what's correct.
    if (backToListUrl && listPage !== listUrlPage) {
      navigate(backToListUrl, { replace: true });
      return;
    }
    if (location.key && location.key !== 'default') {
      navigate(-1);
    } else {
      navigate(backToListUrl || '/facilities');
    }
  }, [navigate, location.key, backToListUrl, listPage, listUrlPage]);

  // The breadcrumb's leading segment comes from the same nav tree the sidebar
  // renders, so a group an operator renames in RBAC Admin reads the same here
  // instead of drifting from a hardcoded "Administration".
  const navigationTree = useNavigationTree();
  const breadcrumbGroup = useMemo(() => {
    const match = navigationTree.find((group) =>
      (group.items || []).some((item) => item.path === '/facilities'));
    return match?.group || null;
  }, [navigationTree]);

  // What the pager is walking, spelled out — a filtered run must not read as
  // though it were the whole directory.
  const listScopeLabel = useMemo(() => {
    const query = listContext?.query || {};
    if (query.search) return `matching "${query.search}"`;
    const filterKeys = ['state', 'status', 'branch_status', 'facility_type_id', 'facility_tier_id'];
    if (filterKeys.some((k) => query[k] != null && query[k] !== '')) return 'in the filtered directory';
    return 'in the directory';
  }, [listContext]);

  const fetchFacility = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await lib.getFacilityById(facilityId);
      const body = resp?.data;
      const hasError = body && (Number(body?.error) === 1 || body?.error === true);
      if (hasError) {
        setFacility(navFacility || null);
        setError(body?.message || 'Failed to load facility');
      } else if (body?.data) {
        setFacility((prev) => mergeFacilityCounts(prev, body.data, facilityId));
      } else if (body && (body.id || body.facility_id || body.name || body.facility_name)) {
        setFacility((prev) => mergeFacilityCounts(prev, body, facilityId));
      } else {
        setFacility(navFacility || null);
        setError(body?.message || 'Failed to load facility');
      }
    } catch (e) {
      setFacility(navFacility || null);
      setError(e?.response?.data?.message || e?.message || 'Failed to load facility');
    } finally {
      setLoading(false);
    }
  }, [facilityId, navFacility]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (!canStayOnModule('facilities', [1, 3, 4, 5])) {
      navigate('/facilities');
      return;
    }
    if (!facilityId) {
      navigate('/facilities');
      return;
    }
    fetchFacility();
  }, [user, facilityId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setUsersScopeId(null);
    setUsersGroup(null);
    if (!facilityId) return undefined;
    let cancelled = false;
    lib.getFacilityBranchesV2(facilityId)
      .then((resp) => {
        const parsed = lib.parseApiEnvelope(resp);
        if (!cancelled && parsed.ok) setUsersGroup(parsed.data || null);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [facilityId]);

  const usersFacilityOptions = useMemo(() => {
    const selfName = facility?.facility_name || facility?.name || `Facility #${facilityId}`;
    const branches = usersGroup?.branches || [];
    if (!usersGroup || usersGroup.is_branch || branches.length === 0) {
      return [{ value: String(facilityId), label: selfName, isBranch: !!usersGroup?.is_branch }];
    }
    return [
      { value: String(facilityId), label: `${selfName} (main)`, isBranch: false },
      ...branches.map((b) => ({
        value: String(b.facility_id ?? b.id),
        label: b.facility_name || b.name || `Branch #${b.facility_id ?? b.id}`,
        isBranch: true,
      })),
    ];
  }, [usersGroup, facility, facilityId]);
  const isBranchFacilityId = useCallback(
    (id) => !!usersFacilityOptions.find((o) => o.value === String(id))?.isBranch,
    [usersFacilityOptions],
  );
  const facilityLabelFor = useCallback(
    (id) => usersFacilityOptions.find((o) => o.value === String(id))?.label || `Facility #${id}`,
    [usersFacilityOptions],
  );

  const fetchUsers = useCallback(async () => {
    if (!usersFacilityId) return;
    setUsersLoading(true);
    setUsersError(null);
    try {
      // The endpoint is paginated (100 rows per page at most); walk every
      // page so the tab holds the whole roster, not just the first slice.
      const archiveParam = showArchivedUsers ? { archived: 1 } : {};
      const resp = await lib.getFacilityUsers(usersFacilityId, { page: 1, limit: 100, ...archiveParam });
      const body = resp?.data ?? resp;
      let data = Array.isArray(body?.data) ? body.data : [];
      const totalPages = Number(body?.pagination?.pages) || 1;
      const maxPages = Math.min(totalPages, 20);
      for (let page = 2; page <= maxPages; page += 1) {
        const nextResp = await lib.getFacilityUsers(usersFacilityId, { page, limit: 100, ...archiveParam });
        const nextBody = nextResp?.data ?? nextResp;
        const nextData = Array.isArray(nextBody?.data) ? nextBody.data : [];
        if (nextData.length === 0) break;
        data = data.concat(nextData);
      }
      setUsers(data);
      if (body?.message && Number(body?.error) !== 0) setUsersError(body.message);
      return data;
    } catch (e) {
      setUsersError(e?.response?.data?.message || e?.message || 'Failed to load users');
      setUsers([]);
      return [];
    } finally {
      setUsersLoading(false);
    }
  }, [usersFacilityId, showArchivedUsers]);

  useEffect(() => {
    setUsersPage(1);
  }, [usersFacilityId]);

  useEffect(() => {
    const pages = Math.max(1, Math.ceil(users.length / usersPageSize));
    if (usersPage > pages) {
      setUsersPage(pages);
    }
  }, [users.length, usersPageSize, usersPage]);

  const openFacilityUserDrawer = useCallback((facilityUser) => {
    setActiveFacilityUser(facilityUser);
    setUserDrawerOpen(true);
  }, []);

  const closeFacilityUserDrawer = useCallback(() => {
    setUserDrawerOpen(false);
    setActiveFacilityUser(null);
  }, []);

  const openCreateUserModal = useCallback(() => {
    createUserForm.resetFields();
    // Defaults to the facility the list is showing; any of the group can be picked.
    createUserForm.setFieldsValue({ target_facility_id: String(usersFacilityId) });
    setCreateUserOpen(true);
  }, [createUserForm, usersFacilityId]);

  const closeCreateUserModal = useCallback(() => {
    setCreateUserOpen(false);
  }, []);

  const handleCreateUserSubmit = useCallback(async (values) => {
    const targetId = values.target_facility_id || usersFacilityId;
    if (!targetId) return;
    setCreateUserSaving(true);
    try {
      const resp = await lib.createFacilityUser(targetId, {
        full_name: String(values.full_name || '').trim(),
        phone: String(values.phone || '').trim() || undefined,
        facility_role_id: values.facility_role_id,
        password: values.password,
      });
      const parsed = lib.parseApiEnvelope(resp);
      if (!parsed.ok) {
        notification.error({ message: parsed.message || 'Unable to create facility user' });
        return;
      }
      closeCreateUserModal();
      // Show the list the user was created in.
      if (String(targetId) !== String(usersFacilityId)) {
        setUsersScopeId(String(targetId) === String(facilityId) ? null : String(targetId));
      } else {
        await fetchUsers();
      }
      const notice = branchNoticeMessage(parsed.data?.branch_notification || parsed.body?.data?.branch_notification);

      // Shown in a modal that must be acknowledged rather than a toast that
      // fades: for a user with no email address, this is the only place the code
      // exists until someone opens the Users tab.
      const username =
        parsed.data?.username || parsed.data?.facility_users_username || parsed.body?.username || null;

      if (username) {
        Modal.success({
          title: 'Facility user created',
          okText: 'I have noted the code',
          content: (
            <div>
              <p style={{ marginBottom: 8 }}>
                <strong>{String(values.full_name || '').trim()}</strong> signs in with this code:
              </p>
              <p style={{ marginBottom: 8 }}>
                <Typography.Text code copyable style={{ fontSize: 20, letterSpacing: 1 }}>
                  {username}
                </Typography.Text>
              </p>
              <p style={{ marginBottom: 0, opacity: 0.7 }}>
                Give it to them with the password you set. It is listed against their
                name below if they forget it.
              </p>
              {notice ? (
                <Alert style={{ marginTop: 12 }} type={notice.type} showIcon message={notice.text} />
              ) : null}
            </div>
          ),
        });
      } else {
        notification.success({ message: parsed.message || 'Facility user created', description: notice?.text });
      }
    } catch (e) {
      notification.error({
        message: e?.response?.data?.message || e?.message || 'Unable to create facility user',
      });
    } finally {
      setCreateUserSaving(false);
    }
  }, [facilityId, usersFacilityId, closeCreateUserModal, fetchUsers]);

  const openPasswordModal = useCallback((user) => {
    passwordForm.resetFields();
    setPasswordTarget(user);
  }, [passwordForm]);

  const closePasswordModal = useCallback(() => {
    setPasswordTarget(null);
  }, []);

  const handlePasswordSubmit = useCallback(async (values) => {
    const userId = passwordTarget?.id ?? passwordTarget?.user_id;
    if (!usersFacilityId || userId == null) return;
    setPasswordSaving(true);
    try {
      const parsed = await lib.setFacilityUserPassword(usersFacilityId, userId, values.password);
      if (!parsed.ok) {
        notification.error({ message: parsed.message || 'Unable to set the password' });
        return;
      }
      notification.success({
        message: parsed.message || 'Password updated',
        description: parsed.data?.username
          ? `${passwordTarget?.name ?? 'This user'} signs in with ${parsed.data.username}.`
          : undefined,
      });
      closePasswordModal();
    } catch (e) {
      notification.error({
        message: e?.response?.data?.message || e?.message || 'Unable to set the password',
      });
    } finally {
      setPasswordSaving(false);
    }
  }, [usersFacilityId, passwordTarget, closePasswordModal]);

  const handleFacilityUserSave = useCallback(async (values) => {
    const userId = activeFacilityUser?.id ?? activeFacilityUser?.user_id;
    if (!usersFacilityId || userId == null) return;
    setUserProfileSaving(true);
    try {
      const resp = await lib.updateFacilityUser(usersFacilityId, userId, {
        full_name: String(values.full_name || '').trim(),
        email: String(values.email || '').trim().toLowerCase(),
        phone: String(values.phone || '').trim(),
      });
      const parsed = lib.parseApiEnvelope(resp);
      if (!parsed.ok) {
        notification.error({ message: parsed.message || 'Unable to update facility user' });
        return;
      }

      const newPassword = String(values.password || '');
      if (newPassword) {
        const passwordResp = await lib.changeFacilityUserPassword(usersFacilityId, userId, {
          password: newPassword,
          confirm_password: String(values.confirm_password || ''),
        });
        const passwordParsed = lib.parseApiEnvelope(passwordResp);
        if (!passwordParsed.ok) {
          notification.error({
            message: passwordParsed.message || 'User saved, but password was not changed',
          });
          return;
        }
      }

      notification.success({
        message: newPassword
          ? 'Facility user and password updated'
          : (parsed.message || 'Facility user updated'),
      });
      closeFacilityUserDrawer();
      await fetchUsers();
    } catch (e) {
      notification.error({
        message: e?.response?.data?.message || e?.message || 'Unable to update facility user',
      });
    } finally {
      setUserProfileSaving(false);
    }
  }, [activeFacilityUser, closeFacilityUserDrawer, usersFacilityId, fetchUsers]);

  // Archive (spec §6) — permanent, there is no unarchive. Soft: the account
  // keeps its login code and history; it can no longer sign in and its email
  // is freed for reuse (e.g. so the facility can move its admin email).
  const handleFacilityUserArchive = useCallback(async (facilityUser) => {
    const userId = facilityUser?.id ?? facilityUser?.user_id;
    if (!usersFacilityId || userId == null) return;
    setUserArchiveSavingId(userId);
    try {
      const resp = await lib.archiveFacilityUser(usersFacilityId, userId);
      const parsed = lib.parseApiEnvelope(resp);
      if (!parsed.ok) {
        notification.error({ message: parsed.message || 'Unable to archive user' });
        return;
      }
      notification.success({ message: parsed.message || 'User archived' });
      await fetchUsers();
    } finally {
      setUserArchiveSavingId(null);
    }
  }, [usersFacilityId, fetchUsers]);

  const handleFacilityUserStatus = useCallback(async (facilityUser, nextStatus) => {
    const userId = facilityUser?.id ?? facilityUser?.user_id;
    if (!usersFacilityId || userId == null) return;
    setUserStatusSavingId(userId);
    try {
      const resp = await lib.updateFacilityUserStatus(usersFacilityId, userId, nextStatus);
      const parsed = lib.parseApiEnvelope(resp);
      if (!parsed.ok) {
        notification.error({ message: parsed.message || 'Unable to update status' });
        return;
      }
      notification.success({
        message: parsed.message || (nextStatus ? 'User activated' : 'User deactivated'),
      });
      await fetchUsers();
    } catch (e) {
      notification.error({
        message: e?.response?.data?.message || e?.message || 'Unable to update status',
      });
    } finally {
      setUserStatusSavingId(null);
    }
  }, [usersFacilityId, fetchUsers]);

  const loadFacilityRoles = useCallback(async () => {
    setFacilityRolesLoading(true);
    try {
      const resp = await listRoles({ user_type: 'facility' });
      const payload = resp?.data || resp;
      const records = payload?.data || payload?.items || payload || [];
      setFacilityRoles(Array.isArray(records) ? records : []);
    } catch (e) {
      notification.error({
        message: e?.response?.data?.message || e?.message || 'Unable to load facility roles',
      });
    } finally {
      setFacilityRolesLoading(false);
    }
  }, []);

  const openRoleModal = useCallback((facilityUser) => {
    setRoleModalUser(facilityUser);
    roleForm.resetFields();
    setRoleModalOpen(true);
    if (facilityRoles.length === 0) loadFacilityRoles();
  }, [facilityRoles.length, loadFacilityRoles, roleForm]);

  const closeRoleModal = useCallback(() => {
    setRoleModalOpen(false);
    setRoleModalUser(null);
    roleForm.resetFields();
  }, [roleForm]);

  const handleAssignFacilityRole = useCallback(async (values) => {
    const userId = roleModalUser?.id ?? roleModalUser?.user_id;
    if (userId == null) return;
    setRoleModalLoading(true);
    try {
      const payload = { role_id: values.role_id };
      if (values.context_note) payload.context_data = { note: values.context_note };
      await assignRoleToFacilityUser(userId, payload);
      notification.success({ message: 'Role assigned successfully' });
      roleForm.resetFields();
      const updated = await fetchUsers();
      const refreshed = updated?.find((u) => Number(u?.id ?? u?.user_id) === Number(userId));
      if (refreshed) setRoleModalUser(refreshed);
    } catch (e) {
      notification.error({
        message: 'Failed to assign role',
        description: e?.response?.data?.message || e?.message,
      });
    } finally {
      setRoleModalLoading(false);
    }
  }, [roleModalUser, fetchUsers, roleForm]);

  const handleRemoveFacilityRole = useCallback(async (role) => {
    const userId = roleModalUser?.id ?? roleModalUser?.user_id;
    const roleId = role?.id ?? role?.role_id;
    if (userId == null || roleId == null) return;
    setRoleModalLoading(true);
    try {
      await removeRoleFromFacilityUser(userId, roleId);
      notification.success({ message: 'Role removed successfully' });
      const updated = await fetchUsers();
      const refreshed = updated?.find((u) => Number(u?.id ?? u?.user_id) === Number(userId));
      if (refreshed) setRoleModalUser(refreshed);
    } catch (e) {
      notification.error({
        message: 'Failed to remove role',
        description: e?.response?.data?.message || e?.message,
      });
    } finally {
      setRoleModalLoading(false);
    }
  }, [roleModalUser, fetchUsers]);

  const facilityRoleOptions = useMemo(() => (
    facilityRoles.map((role) => ({
      value: role.id,
      label: role.display_name || role.name,
    }))
  ), [facilityRoles]);

  const fetchTariffs = useCallback(async () => {
    if (!facilityId) return;
    setTariffsLoading(true);
    setTariffsError(null);
    try {
      const resp = await lib.getAdminFacilityTariffs(facilityId, { limit: 50, offset: 0 });
      const body = resp?.data;
      if (body?.error) {
        setTariffsError(body?.message || 'Failed to load tariffs');
        setTariffs([]);
      } else {
        const data = body?.data ?? body;
        const list = Array.isArray(data?.tariffs) ? data.tariffs : [];
        setTariffs(list);
      }
    } catch (e) {
      setTariffsError(e?.response?.data?.message || e?.message || 'Failed to load tariffs');
      setTariffs([]);
    } finally {
      setTariffsLoading(false);
    }
  }, [facilityId]);

  const fetchActiveTariff = useCallback(async () => {
    if (!facilityId) return;
    setActiveTariffLoading(true);
    try {
      const getActive = lib.getAdminFacilityActiveTariff || lib.getFacilityActiveTariff;
      const resp = await getActive(facilityId);
      const body = resp?.data;
      if (body?.error) setActiveTariff(null);
      else {
        const data = body?.data ?? body;
        setActiveTariff(data?.tariff ?? data ?? null);
      }
    } catch {
      try {
        const resp = await lib.getFacilityActiveTariff(facilityId);
        const data = resp?.data?.data ?? resp?.data;
        setActiveTariff(data?.tariff ?? data ?? null);
      } catch {
        setActiveTariff(null);
      }
    } finally {
      setActiveTariffLoading(false);
    }
  }, [facilityId]);

  const fetchAccesses = useCallback(async () => {
    if (!facilityId) return;
    setAccessesLoading(true);
    setAccessesError(null);
    try {
      const resp = await lib.getFacilityAccesses(facilityId);
      const body = resp?.data;
      const data = Array.isArray(body?.data) ? body.data : body?.data ? [body.data] : [];
      setAccesses(Array.isArray(data) ? data : []);
    } catch (e) {
      setAccessesError(e?.response?.data?.message || e?.message || 'Failed to load accesses');
      setAccesses([]);
    } finally {
      setAccessesLoading(false);
    }
  }, [facilityId]);

  useEffect(() => {
    if (activeTab === 'users') fetchUsers();
  }, [activeTab, fetchUsers]);
  useEffect(() => {
    if (activeTab === 'tariffs') {
      fetchTariffs();
      fetchActiveTariff();
    }
  }, [activeTab, fetchTariffs, fetchActiveTariff]);
  useEffect(() => {
    if (activeTab === 'access') fetchAccesses();
  }, [activeTab, fetchAccesses]);

  const closeProfileModal = () => {
    setShowProfileModal(false);
    fetchFacility();
  };

  const getTariffId = (r) => r?.id ?? r?.tariff_id ?? r?.facility_tariff_id;
  const handleSetActiveTariff = useCallback(async (record) => {
    const id = getTariffId(record);
    if (!facilityId || !id) return;
    setSetActiveLoadingId(id);
    try {
      const setActive = lib.setAdminFacilityActiveTariff || lib.setFacilityActiveTariff;
      const resp = await setActive(facilityId, { source: 'specific', facility_tariff_id: Number(id) });
      if (resp?.data?.error) throw new Error(resp.data.message || 'Failed to set active');
      await fetchActiveTariff();
      await fetchTariffs();
      notification.success({ message: 'Active tariff updated', placement: 'topRight' });
    } catch (e) {
      notification.error({
        message: e?.response?.data?.message || e?.message || 'Failed to set active tariff',
        placement: 'topRight',
      });
    } finally {
      setSetActiveLoadingId(null);
    }
  }, [facilityId, fetchActiveTariff, fetchTariffs]);

  const activeTariffId = activeTariff?.facility_tariff_id ?? activeTariff?.id ?? activeTariff?.tariff_id;

  // Grouped rather than one flat run of fields: a reader looking for a phone
  // number should not have to scan past Facility ID and row counts to find it.
  // Status, Type and Tier are deliberately absent — they are pills in the header
  // now, and the counts live in the metric tiles, so nothing is stated twice.
  const infoGroups = useMemo(() => {
    if (!facility) return [];
    const code = getFacilityCode(facility);
    const groups = [
      {
        key: 'contact',
        title: 'Contact',
        icon: <MailOutlined />,
        fields: [
          { label: 'Phone', value: fieldOrDash(facility.contact_phone, providerRecord.contact_phone, providerRecord.facility_phone) },
          { label: 'Email', value: fieldOrDash(facility.contact_email, providerRecord.contact_email, providerRecord.facility_email) },
          { label: 'Contact person', value: providerRecord.facility_designation, optional: true },
          { label: 'Website', value: providerRecord.facility_website, optional: true },
        ],
      },
      {
        key: 'location',
        title: 'Location',
        icon: <EnvironmentOutlined />,
        fields: [
          { label: 'Address', value: fieldOrDash(facility.address, providerRecord.facility_address) },
          { label: 'City', value: fieldOrDash(facility.city, providerRecord.facility_city) },
          { label: 'State', value: fieldOrDash(facility.state, providerRecord.facility_state) },
        ],
      },
      {
        key: 'record',
        title: 'Record',
        icon: <FileTextOutlined />,
        fields: [
          { label: 'Facility ID', value: fieldOrDash(facility.id, facility.facility_id, facilityId) },
          { label: 'Code', value: code },
          { label: 'Created', value: formatDateTime(facility.created_at ?? providerRecord.facility_date) },
          { label: 'Updated', value: formatDateTime(facility.updated_at) },
          { label: 'Onboarded by', value: providerRecord.facility_staff_id, optional: true },
        ],
      },
    ];
    // An optional field with nothing in it is noise; a core one shows an em dash
    // so its absence is visible rather than silently missing.
    return groups
      .map((g) => ({
        ...g,
        fields: g.fields.filter((f) => !f.optional || (f.value != null && f.value !== '')),
      }))
      .filter((g) => g.fields.length > 0);
  }, [facility, facilityId, providerRecord]);

  // Facts the identity header shows. Resolved here so the markup stays flat and
  // each falls back across the v2 record and the legacy provider shape.
  const identityLocation = useMemo(
    () => [facility?.city ?? providerRecord.facility_city, facility?.state ?? providerRecord.facility_state]
      .filter(Boolean)
      .join(', '),
    [facility, providerRecord],
  );
  const identityPhone = useMemo(() => {
    const v = fieldOrDash(facility?.contact_phone, providerRecord.contact_phone, providerRecord.facility_phone);
    return v === '—' ? '' : v;
  }, [facility, providerRecord]);
  const identityType = useMemo(() => {
    const v = fieldOrDash(providerRecord.facility_type, facility?.facility_type, facility?.type);
    return v === '—' ? '' : v;
  }, [facility, providerRecord]);
  const identityTier = useMemo(() => {
    const v = fieldOrDash(providerRecord.facility_plan_type, facility?.plan_type);
    return v === '—' ? '' : v;
  }, [facility, providerRecord]);

  // Fixed set, always rendered in this order, so the band keeps its height as
  // you step through the directory instead of appearing and vanishing.
  const heroMetrics = useMemo(() => {
    if (!facility) return [];
    return [
      {
        key: 'users',
        title: 'Users',
        value: facility.users_count,
        color: '#3497D3',
        icon: <UserOutlined />,
        subtitle: 'Portal accounts',
      },
      {
        key: 'tariffs',
        title: 'Tariffs',
        value: facility.tariffs_count,
        color: '#722ed1',
        icon: <DollarOutlined />,
        subtitle: 'Pricing schedules',
      },
      {
        key: 'appointments',
        title: 'Appointments',
        value: facility.appointments_count,
        color: '#49B870',
        icon: <FileTextOutlined />,
        subtitle: 'Scheduled visits',
      },
      {
        key: 'claims',
        title: 'Claims',
        value: facility.claims_count,
        color: '#0ea5e9',
        icon: <CheckCircleOutlined />,
        subtitle: 'Submitted claims',
      },
    ].map((m) => ({ ...m, value: hasMetricValue(m.value) ? m.value : 0 }));
  }, [facility]);

  if (!facilityId) return null;

  return (
    <div className="facilities-dashboard facility-detail-page">
      <div className="dashboard-page-stack">
        {/* Where you are, and how to move around the directory. Both belong to
            the *list*, not to this facility — keeping them here leaves the
            header row for actions that act on the facility itself. */}
        <div className="facility-detail-crumbrow">
          <nav className="facility-detail-breadcrumb" aria-label="Breadcrumb">
            {breadcrumbGroup ? (
              <>
                <span className="facility-detail-breadcrumb__group">{breadcrumbGroup}</span>
                <span aria-hidden>/</span>
              </>
            ) : null}
            <Button type="link" className="facility-detail-breadcrumb__link" onClick={goBackToList}>
              Facilities
            </Button>
            <span aria-hidden>/</span>
            <span className="facility-detail-breadcrumb__current" aria-current="page">
              {facility ? getFacilityName(facility) : 'Detail'}
            </span>
          </nav>

          <div className="facility-detail-crumbrow__nav">
            {/* Prev/next only mean something when the page was opened from the
                directory — that is the only case with an order to follow. */}
            {listContext && listPosition > 0 && listTotal > 0 ? (
              <span className="facility-detail-crumbrow__position">
                {listPosition} of {listTotal} {listScopeLabel}
              </span>
            ) : null}
            <Button size="small" icon={<ArrowLeftOutlined />} onClick={goBackToList}>
              Back to list
            </Button>
            {listContext ? (
              <>
                <Tooltip title={hasPrevFacility ? 'Open the previous facility in the directory' : 'This is the first facility in the current list'}>
                  <span>
                    <Button
                      size="small"
                      onClick={() => stepFacility(-1)}
                      loading={stepping === -1}
                      disabled={!hasPrevFacility || stepping !== 0}
                    >
                      &lsaquo; Previous facility
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title={hasNextFacility ? 'Open the next facility in the directory' : 'This is the last facility in the current list'}>
                  <span>
                    <Button
                      size="small"
                      onClick={() => stepFacility(1)}
                      loading={stepping === 1}
                      disabled={!hasNextFacility || stepping !== 0}
                    >
                      Next facility &rsaquo;
                    </Button>
                  </span>
                </Tooltip>
              </>
            ) : null}
          </div>
        </div>

        <header className="facility-identity">
          <div className="facility-identity__lead">
            <div
              className="facility-identity__monogram"
              style={{ '--monogram-color': getMonogramColor(facility || {}) }}
              aria-hidden
            >
              {facility ? getFacilityInitials(facility) : <BankOutlined />}
            </div>
            <div className="facility-identity__headings">
              <div className="facility-identity__nameline">
                <h1 className="facility-identity__name">
                  {facility ? getFacilityName(facility) : 'Facility detail'}
                </h1>
                {facility ? (
                  <Tag
                    className="facility-identity__status"
                    color={isFacilityActive(facility) ? 'success' : 'default'}
                  >
                    {isFacilityActive(facility) ? 'Active' : 'Inactive'}
                  </Tag>
                ) : null}
              </div>

              {/* Identifier line: who this is, not what it is classified as. */}
              <p className="facility-identity__sub">
                <span className="facility-identity__code">{getFacilityCode(facility || {})}</span>
                {facility ? (
                  <>
                    {identityLocation ? (
                      <>
                        <span className="facility-identity__dot" aria-hidden>·</span>
                        <EnvironmentOutlined aria-hidden /> {identityLocation}
                      </>
                    ) : null}
                    {identityPhone ? (
                      <>
                        <span className="facility-identity__dot" aria-hidden>·</span>
                        {identityPhone}
                      </>
                    ) : null}
                  </>
                ) : null}
              </p>

              {facility ? (
                <div className="facility-identity__pills">
                  {identityType ? (
                    <span className="facility-pill">
                      <BankOutlined aria-hidden /> {identityType}
                    </span>
                  ) : null}
                  {identityTier ? (
                    <span className="facility-pill facility-pill--tier">
                      <ApartmentOutlined aria-hidden /> {identityTier}
                    </span>
                  ) : null}
                  <Tooltip
                    title={
                      isFacilityListed(facility)
                        ? 'Visible on the website, client portal, and mobile app. This is separate from Active/Inactive — a utility facility can stay active and still be hidden from clients.'
                        : 'Hidden from clients. Staff can still check patients in and submit claims here — this only affects public visibility.'
                    }
                  >
                    <span className="facility-identity__listed">
                      <Switch
                        size="small"
                        checked={isFacilityListed(facility)}
                        onChange={handleListedToggle}
                        loading={listedSaving}
                        disabled={!canManageProfile}
                      />
                      <span>{isFacilityListed(facility) ? 'Shown to clients' : 'Hidden from clients'}</span>
                    </span>
                  </Tooltip>
                </div>
              ) : null}
            </div>
          </div>

          {/* Actions on *this facility* only. Back / prev / next moved up to the
              breadcrumb row, and Refresh is gone — the browser already does it. */}
          <div className="facility-identity__actions">
            {canSendLoginDetails ? (
              <Button
                icon={<MailOutlined />}
                onClick={confirmSendLoginDetails}
                loading={onboardingLoading}
              >
                Send login details
              </Button>
            ) : null}
            {canManageProfile ? (
              <Button type="primary" icon={<EditOutlined />} onClick={() => setShowProfileModal(true)}>
                Manage profile
              </Button>
            ) : null}
          </div>
        </header>

        {error && !facility ? (
          <Alert type="error" showIcon message="Error" description={error} />
        ) : null}

        {loading && !facility ? (
          <div className="facilities-loading">
            <Spin size="large" />
          </div>
        ) : (
          <>
            {heroMetrics.length > 0 ? (
              <section className="facilities-overview dash-overview dash-overview--compact" aria-label="Facility summary">
                <DashboardHero metrics={heroMetrics} loading={loading && !facility} />
              </section>
            ) : null}

            <section className="facility-detail-panel">
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              className="facility-detail-tabs"
              items={[
                {
                  key: 'info',
                  label: (<span><FileTextOutlined /> Profile</span>),
                  children: (
                    <DashboardSection
                      id="facility-profile"
                      icon={<FileTextOutlined />}
                      title="Facility profile"
                      subtitle={canManageProfile ? 'Contact, location and record details' : 'Read-only facility information'}
                      collapsible={false}
                      variant="content"
                    >
                      {facility ? (
                        <div className="facility-profile-groups">
                          {infoGroups.map((group) => (
                            <section key={group.key} className="facility-profile-group">
                              <h3 className="facility-profile-group__title">
                                <span className="facility-profile-group__icon" aria-hidden>{group.icon}</span>
                                {group.title}
                              </h3>
                              <dl className="facility-profile-group__fields">
                                {group.fields.map(({ label, value }) => (
                                  <div key={label} className="facility-field">
                                    <dt className="facility-field__label">{label}</dt>
                                    <dd className="facility-field__value">{value}</dd>
                                  </div>
                                ))}
                              </dl>
                            </section>
                          ))}
                        </div>
                      ) : (
                        <Empty description="No facility data" />
                      )}
                      {facility && canViewAccountDetails ? (
                        <FacilityAccountDetailsSection
                          facilityId={facilityId}
                          facilityName={getFacilityName(facility)}
                        />
                      ) : null}
                      {canActivateDisable || canSendLoginDetails ? (
                        <p className="facility-detail-hint">
                          Use <strong>Send login details</strong> at the top of this page to email portal credentials
                          {canActivateDisable ? (
                            <>
                              , or <strong>Manage profile</strong> to edit details and activate or disable this facility
                            </>
                          ) : null}
                          . Passwords are never shown here.
                        </p>
                      ) : null}
                    </DashboardSection>
                  ),
                },
                {
                  key: 'users',
                  label: (<span><UserOutlined /> Users ({users.length})</span>),
                  children: (
                    <DashboardSection
                      id="facility-users"
                      icon={<UserOutlined />}
                      title="Facility users"
                      subtitle="Portal accounts linked to this facility"
                      collapsible={false}
                      variant="content"
                      action={canManageFacilityUsers ? (
                        <Space size={8} wrap>
                          {usersFacilityOptions.length > 1 ? (
                            <Select
                              size="small"
                              style={{ minWidth: 220 }}
                              aria-label="Show users for"
                              value={String(usersFacilityId)}
                              onChange={(v) => setUsersScopeId(v === String(facilityId) ? null : v)}
                              options={usersFacilityOptions.map(({ value, label }) => ({ value, label }))}
                            />
                          ) : null}
                          <Segmented
                            size="small"
                            value={showArchivedUsers ? 'archived' : 'active'}
                            onChange={(v) => setShowArchivedUsers(v === 'archived')}
                            options={[
                              { label: 'Active', value: 'active' },
                              { label: 'Archived', value: 'archived' },
                            ]}
                          />
                          <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openCreateUserModal}>
                            Add user
                          </Button>
                        </Space>
                      ) : null}
                    >
                      {usersError ? <Alert type="warning" message={usersError} className="facility-tab-alert" /> : null}
                      {usersLoading ? (
                        <div className="facilities-loading"><Spin /></div>
                      ) : users.length === 0 ? (
                        <Empty description={usersFacilityOptions.length > 1 ? `No users for ${facilityLabelFor(usersFacilityId)}` : 'No users for this facility'} />
                      ) : (
                        <>
                        <div className="facilities-listing-stack">
                          {users
                            .slice((usersPage - 1) * usersPageSize, usersPage * usersPageSize)
                            .map((u) => {
                            const userKey = u?.id ?? u?.user_id ?? u?.email;
                            const isActive = Number(u?.status) === 1;
                            const isArchived = Boolean(u?.archived);
                            const statusKnown = !isArchived && u?.status != null && u?.status !== '';
                            return (
                              <article key={userKey} className="listing-card listing-card--linear facility-user-row">
                                <div className="listing-card__lead">
                                  <div className="listing-card__icon is-staff" aria-hidden>
                                    <UserOutlined />
                                  </div>
                                  <div className="listing-card__identity">
                                    <h3 className="listing-card__title">{u?.name ?? u?.full_name ?? u?.email ?? '—'}</h3>
                                    {/* The login code, first: it is what this person
                                        signs in with, and for a user with no email it
                                        is the only handle anyone has on them. */}
                                    <p className="listing-card__meta">
                                      {u?.username ? (
                                        <Typography.Text code copyable={{ text: u.username }}>
                                          {u.username}
                                        </Typography.Text>
                                      ) : (
                                        <Tooltip title="This user predates login codes. Run the backfill, or set one by editing them.">
                                          <Typography.Text type="secondary">No login code yet</Typography.Text>
                                        </Tooltip>
                                      )}
                                    </p>
                                    {u?.email ? (
                                      <p className="listing-card__meta">{u.email}</p>
                                    ) : null}
                                    {u?.phone ? (
                                      <p className="listing-card__meta">{u.phone}</p>
                                    ) : null}
                                  </div>
                                </div>
                                <div className="listing-card__facts">
                                  <span className="listing-card__fact">{u?.role ?? '—'}</span>
                                  {isArchived ? (
                                    <Tooltip title={u?.archive_reason || undefined}>
                                      <Tag>Archived</Tag>
                                    </Tooltip>
                                  ) : statusKnown ? (
                                    <Tag color={isActive ? 'success' : 'default'}>
                                      {isActive ? 'Active' : 'Inactive'}
                                    </Tag>
                                  ) : null}
                                </div>
                                {canManageFacilityUsers && !isArchived ? (
                                  <div className="listing-card__aside" onClick={(e) => e.stopPropagation()}>
                                    <Button
                                      type="link"
                                      size="small"
                                      icon={<EditOutlined />}
                                      onClick={() => openFacilityUserDrawer(u)}
                                    >
                                      Edit
                                    </Button>
                                    {canManageFacilityRoles ? (
                                      <Button
                                        type="link"
                                        size="small"
                                        icon={<TeamOutlined />}
                                        onClick={() => openRoleModal(u)}
                                      >
                                        Roles
                                      </Button>
                                    ) : null}
                                    {/* An administrator setting the password is the
                                        only recovery route these users have: they may
                                        have no email address to send a reset link to.
                                        Gated separately from Edit because it hands
                                        over account access rather than editing a
                                        record. */}
                                    {canSetFacilityUserPassword ? (
                                      <Button
                                        type="link"
                                        size="small"
                                        icon={<KeyOutlined />}
                                        onClick={() => openPasswordModal(u)}
                                      >
                                        Password
                                      </Button>
                                    ) : null}
                                    {statusKnown ? (
                                      <Popconfirm
                                        title={isActive ? 'Deactivate this user?' : 'Activate this user?'}
                                        onConfirm={() => handleFacilityUserStatus(u, isActive ? 0 : 1)}
                                        okText="Yes"
                                        cancelText="No"
                                      >
                                        <Button
                                          type="link"
                                          size="small"
                                          loading={userStatusSavingId === (u?.id ?? u?.user_id)}
                                        >
                                          {isActive ? 'Deactivate' : 'Activate'}
                                        </Button>
                                      </Popconfirm>
                                    ) : null}
                                    {isArchived ? null : (
                                      <Popconfirm
                                        title="Archive this user?"
                                        description={(
                                          <span style={{ display: 'block', maxWidth: 300 }}>
                                            They can no longer sign in, and their email is freed for reuse.
                                            Their claims, requests and history are kept.{' '}
                                            <strong>This cannot be undone.</strong>
                                          </span>
                                        )}
                                        onConfirm={() => handleFacilityUserArchive(u)}
                                        okText="Archive"
                                        okButtonProps={{ danger: true }}
                                        cancelText="Cancel"
                                      >
                                        <Button
                                          type="link"
                                          size="small"
                                          danger
                                          loading={userArchiveSavingId === (u?.id ?? u?.user_id)}
                                        >
                                          Archive
                                        </Button>
                                      </Popconfirm>
                                    )}
                                  </div>
                                ) : null}
                              </article>
                            );
                          })}
                        </div>
                        <Pagination
                          className="facility-users-pagination"
                          current={usersPage}
                          pageSize={usersPageSize}
                          total={users.length}
                          showSizeChanger
                          pageSizeOptions={[10, 20, 50, 100]}
                          showTotal={(total, range) => `${range[0]}–${range[1]} of ${total}`}
                          hideOnSinglePage
                          onChange={(page, pageSize) => {
                            setUsersPage(pageSize !== usersPageSize ? 1 : page);
                            setUsersPageSize(pageSize);
                          }}
                        />
                        </>
                      )}
                    </DashboardSection>
                  ),
                },
                {
                  key: 'tariffs',
                  label: (<span><DollarOutlined /> Tariffs ({tariffs.length})</span>),
                  children: (
                    <DashboardSection
                      id="facility-tariffs"
                      icon={<DollarOutlined />}
                      title="Tariffs"
                      subtitle="Manage pricing schedules for this facility"
                      collapsible={false}
                      variant="content"
                      action={(
                        <Space wrap>
                          <Button size="small" onClick={() => navigate(`/facilities/${facilityId}/tariffs`)}>
                            Manage
                          </Button>
                          <Button
                            type="primary"
                            size="small"
                            onClick={() => navigate(`/facilities/${facilityId}/tariffs`, { state: { openUpload: true } })}
                          >
                            Upload
                          </Button>
                        </Space>
                      )}
                    >
                      {tariffsError ? <Alert type="warning" message={tariffsError} className="facility-tab-alert" /> : null}
                      {tariffsLoading ? (
                        <div className="facilities-loading"><Spin /></div>
                      ) : tariffs.length === 0 ? (
                        <Empty description="No tariffs for this facility" />
                      ) : (
                        <div className="facilities-listing-stack">
                          {tariffs.map((record) => {
                            const id = getTariffId(record);
                            const isActive = id != null && String(id) === String(activeTariffId);
                            return (
                              <article
                                key={id ?? record?.tariff_name}
                                className="listing-card listing-card--linear facility-tariff-row"
                                onClick={id ? () => navigate(`/facilities/${facilityId}/tariffs/${encodeURIComponent(id)}`, { state: { tariff: record } }) : undefined}
                                role={id ? 'button' : undefined}
                                tabIndex={id ? 0 : undefined}
                              >
                                <div className="listing-card__lead">
                                  <div className="listing-card__icon is-facility" aria-hidden>
                                    <DollarOutlined />
                                  </div>
                                  <div className="listing-card__identity">
                                    <h3 className="listing-card__title">{record?.tariff_name ?? record?.name ?? '—'}</h3>
                                    <p className="listing-card__meta">
                                      {record?.category_name ?? record?.category ?? '—'}
                                    </p>
                                  </div>
                                </div>
                                <div className="listing-card__facts">
                                  <span className="listing-card__fact">
                                    {record?.effective_date ? formatDate(record.effective_date) : '—'}
                                  </span>
                                  <span className="listing-card__fact">
                                    {record?.items_count ?? record?.items?.length ?? '—'} items
                                  </span>
                                </div>
                                <div className="listing-card__aside" onClick={(e) => e.stopPropagation()}>
                                  {isActive ? (
                                    <Tag color="green" icon={<CheckCircleOutlined />}>Active</Tag>
                                  ) : (
                                    <Button
                                      type="link"
                                      size="small"
                                      loading={setActiveLoadingId === id}
                                      onClick={() => handleSetActiveTariff(record)}
                                    >
                                      Set active
                                    </Button>
                                  )}
                                  <Button
                                    type="link"
                                    size="small"
                                    onClick={() => navigate(`/facilities/${facilityId}/tariffs/${encodeURIComponent(id)}`, { state: { tariff: record } })}
                                  >
                                    Open
                                  </Button>
                                </div>
                              </article>
                            );
                          })}
                        </div>
                      )}
                    </DashboardSection>
                  ),
                },
                {
                  key: 'branches',
                  label: (
                    <span>
                      <ApartmentOutlined /> Branches
                      {facility?.branch_count ? ` (${facility.branch_count})` : ''}
                    </span>
                  ),
                  children: (
                    <DashboardSection
                      id="facility-branches"
                      icon={<ApartmentOutlined />}
                      title="Branches"
                      subtitle="Facilities operating under this one. Branches share encounter codes with the main facility and roll their encounters up to it."
                      collapsible={false}
                      variant="content"
                    >
                      <FacilityBranchesTab
                        facilityId={facilityId}
                        facilityName={getFacilityName(facility)}
                        canManage={canManageBranches}
                        onChanged={fetchFacility}
                      />
                    </DashboardSection>
                  ),
                },
                {
                  key: 'access',
                  label: (<span><LockOutlined /> Access ({accesses.length})</span>),
                  children: (
                    <DashboardSection
                      id="facility-access"
                      icon={<LockOutlined />}
                      title="Access rules"
                      subtitle="Roles and permissions scoped to this facility (read-only)"
                      collapsible={false}
                      variant="content"
                    >
                      <Alert
                        type="info"
                        showIcon
                        className="facility-tab-alert"
                        message="Access edits are not available here yet"
                        description="Updating facility access rules via this screen is not implemented (API returns 501). Use the RBAC admin tools to change role permissions."
                      />
                      {accessesError ? <Alert type="warning" message={accessesError} className="facility-tab-alert" /> : null}
                      {accessesLoading ? (
                        <div className="facilities-loading"><Spin /></div>
                      ) : accesses.length === 0 ? (
                        <Empty description="No access rules" />
                      ) : (
                        <div className="facilities-listing-stack">
                          {accesses.map((row, i) => (
                            <article key={row?.id ?? row?.role ?? i} className="listing-card listing-card--linear">
                              <div className="listing-card__lead">
                                <div className="listing-card__icon is-facility" aria-hidden>
                                  <LockOutlined />
                                </div>
                                <div className="listing-card__identity">
                                  <h3 className="listing-card__title">{row?.role ?? row?.name ?? '—'}</h3>
                                </div>
                              </div>
                              <div className="listing-card__facts">
                                <span className="listing-card__fact">{row?.scope ?? '—'}</span>
                              </div>
                            </article>
                          ))}
                        </div>
                      )}
                    </DashboardSection>
                  ),
                },
              ]}
            />
            </section>

          </>
        )}
      </div>

      {showProfileModal && (
        <FacilityData
          data={providerRecord}
          display={showProfileModal}
          action={closeProfileModal}
          command={closeProfileModal}
          source={1}
          // "Manage profile" is already the edit entry point — no second Edit click.
          startInEditMode
          modalClassName={ADMIN_REVIEW_MODAL_CLASS}
        />
      )}

      <Drawer
        title={
          activeFacilityUser
            ? `Edit user – ${activeFacilityUser.name || activeFacilityUser.full_name || activeFacilityUser.email || 'Facility user'}`
            : 'Edit facility user'
        }
        open={userDrawerOpen}
        onClose={closeFacilityUserDrawer}
        width={420}
        destroyOnClose
      >
        {activeFacilityUser ? (
          <StaffProfileEditor
            showStaffId={false}
            initialValues={{
              full_name: activeFacilityUser.full_name || activeFacilityUser.name || '',
              email: activeFacilityUser.email || '',
              phone: activeFacilityUser.phone || '',
            }}
            submitting={userProfileSaving}
            onCancel={closeFacilityUserDrawer}
            onSubmit={handleFacilityUserSave}
            passwordMode="admin"
            emailRequired={false}
          />
        ) : (
          <Empty description="Select a facility user to edit." />
        )}
      </Drawer>

      <Modal
        title={usersFacilityOptions.length > 1 && createTargetId ? `Add facility user — ${facilityLabelFor(createTargetId)}` : 'Add facility user'}
        open={createUserOpen}
        onCancel={closeCreateUserModal}
        onOk={() => createUserForm.submit()}
        confirmLoading={createUserSaving}
        okText="Create user"
        destroyOnClose
      >
        <Form
          form={createUserForm}
          layout="vertical"
          onFinish={handleCreateUserSubmit}
          onValuesChange={(changed) => {
            // A branch cannot have the types only a main may have; drop a choice that no longer applies.
            if ('target_facility_id' in changed && isBranchFacilityId(changed.target_facility_id)) {
              const role = createUserForm.getFieldValue('facility_role_id');
              if (role != null && !roleOptionsFor(true).some((o) => o.value === role)) {
                createUserForm.setFieldsValue({ facility_role_id: undefined });
              }
            }
          }}
        >
          <Form.Item
            label="Facility"
            name="target_facility_id"
            hidden={usersFacilityOptions.length <= 1}
            rules={[{ required: true, message: 'Select the facility' }]}
            extra={isBranchFacilityId(createTargetId)
              ? 'Branch users can be Front Desk Officers or Claims Officers. If the branch has an email on its Facility Information, it is emailed the login ID and password.'
              : undefined}
          >
            <Select options={usersFacilityOptions.map(({ value, label }) => ({ value, label }))} />
          </Form.Item>

          <Form.Item
            label="Full name"
            name="full_name"
            rules={[{ required: true, message: 'Full name is required' }]}
          >
            <Input placeholder="Jane Doe" allowClear />
          </Form.Item>

          {/* No email (spec §6): staff sign in with the login code shown after
              saving. Asking for one only led to the facility admin's address
              being reused on staff accounts. */}
          <Form.Item
            label="Phone"
            name="phone"
            extra="Optional."
          >
            <Input type="tel" autoComplete="tel" placeholder="+234 … (optional)" allowClear />
          </Form.Item>

          <Form.Item
            label="Role"
            name="facility_role_id"
            rules={[{ required: true, message: 'Select a role' }]}
          >
            <Select
              placeholder="Select a role"
              options={roleOptionsFor(isBranchFacilityId(createTargetId))}
            />
          </Form.Item>

          <Form.Item
            label="Password"
            name="password"
            rules={[
              { required: true, message: 'Password is required' },
              { min: 8, message: 'Password must be at least 8 characters' },
            ]}
          >
            <Input.Password autoComplete="new-password" placeholder="At least 8 characters" />
          </Form.Item>

          <Form.Item
            label="Confirm password"
            name="confirm_password"
            dependencies={['password']}
            rules={[
              { required: true, message: 'Confirm the password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) return Promise.resolve();
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Set password – ${passwordTarget?.name || passwordTarget?.full_name || 'Facility user'}`}
        open={Boolean(passwordTarget)}
        onCancel={closePasswordModal}
        onOk={() => passwordForm.submit()}
        confirmLoading={passwordSaving}
        okText="Set password"
        destroyOnClose
      >
        <p style={{ marginBottom: 12 }}>
          {passwordTarget?.username ? (
            <>
              This user signs in with{' '}
              <Typography.Text code copyable={{ text: passwordTarget.username }}>
                {passwordTarget.username}
              </Typography.Text>
              . Give them the code and the new password together.
            </>
          ) : (
            'This user has no login code yet. Run the backfill script so they have one to sign in with.'
          )}
        </p>
        <Form form={passwordForm} layout="vertical" onFinish={handlePasswordSubmit}>
          <Form.Item
            label="New password"
            name="password"
            rules={[
              { required: true, message: 'Password is required' },
              { min: 8, message: 'Password must be at least 8 characters' },
            ]}
          >
            <Input.Password autoComplete="new-password" placeholder="At least 8 characters" />
          </Form.Item>
          <Form.Item
            label="Confirm password"
            name="confirm_password"
            dependencies={['password']}
            rules={[
              { required: true, message: 'Confirm the password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) return Promise.resolve();
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={
          <span>
            Manage roles – {roleModalUser?.name || roleModalUser?.full_name || roleModalUser?.email || 'Facility user'}
          </span>
        }
        open={roleModalOpen}
        onCancel={closeRoleModal}
        footer={null}
        destroyOnClose
        width={480}
      >
        <Typography.Title level={5} style={{ marginBottom: 12 }}>
          Assigned roles
        </Typography.Title>
        <List
          dataSource={roleModalUser?.rbac_roles || []}
          locale={{ emptyText: 'No roles assigned yet.' }}
          renderItem={(role) => {
            const roleId = role?.id ?? role?.role_id;
            return (
              <List.Item
                actions={[
                  <Popconfirm
                    key="remove"
                    title="Remove role?"
                    description={`Remove ${role.display_name || role.name || 'role'} from this user.`}
                    onConfirm={() => handleRemoveFacilityRole(role)}
                    okButtonProps={{ loading: roleModalLoading }}
                    disabled={roleModalLoading}
                  >
                    <Button type="link" danger size="small">
                      Remove
                    </Button>
                  </Popconfirm>,
                ]}
              >
                <List.Item.Meta
                  title={role.display_name || role.name || `Role #${roleId}`}
                  description={role.description}
                />
              </List.Item>
            );
          }}
          style={{ marginBottom: 24 }}
        />

        <Typography.Title level={5} style={{ marginBottom: 12 }}>
          Assign new role
        </Typography.Title>
        <Form form={roleForm} layout="vertical" onFinish={handleAssignFacilityRole} disabled={roleModalLoading}>
          <Form.Item
            name="role_id"
            label="Role"
            rules={[{ required: true, message: 'Select a role to assign' }]}
          >
            <Select
              showSearch
              placeholder="Select role"
              optionFilterProp="label"
              options={facilityRoleOptions}
              loading={facilityRolesLoading}
            />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={roleModalLoading}>
            Assign role
          </Button>
        </Form>
      </Modal>
    </div>
  );
}

export default FacilityDetail;
